import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, Modal, Table, StatusIndicator, SpaceBetween, Alert } from '@amzn/awsui-components-react';
import { listQSIngestions } from './Utils/CruzerAPIs';

const RefreshHistoryModal = ({ visible, onDismiss, schedule }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nextToken, setNextToken] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const fetchIngestions = useCallback((token = null, append = false) => {
    if (!schedule?.originalData?.reference_id) return;
    const setter = append ? setLoadingMore : setLoading;
    setter(true);
    setError(null);
    listQSIngestions(
      schedule.originalData.reference_id,
      (response) => {
        try {
          const raw = typeof response.data?.listQSIngestions === 'string'
            ? JSON.parse(response.data.listQSIngestions)
            : response.data?.listQSIngestions;
          const parsed = typeof raw?.body === 'string' ? JSON.parse(raw.body) : raw;
          const ingestions = parsed?.Ingestions || [];
          setHistory(prev => append ? [...prev, ...ingestions] : ingestions);
          setNextToken(parsed?.NextToken || null);
        } catch (e) {
          setError('Failed to parse refresh history response.');
        } finally {
          setter(false);
        }
      },
      () => { setter(false); setError('Failed to load refresh history. Please try again.'); },
      token
    );
  }, [schedule?.originalData?.reference_id]);

  useEffect(() => {
    if (visible && schedule?.originalData?.reference_id) {
      setHistory([]);
      setNextToken(null);
      setError(null);
      fetchIngestions();
    }
  }, [visible, schedule?.originalData?.reference_id, fetchIngestions]);

  const columnDefinitions = [
    {
      id: 'CreatedTime',
      header: 'Created At (Local Time)',
      cell: item => item.CreatedTime ? new Date(item.CreatedTime).toLocaleString() : '—',
      width: 200
    },
    {
      id: 'IngestionStatus',
      header: 'Status',
      cell: item => {
        const statusMap = { COMPLETED: 'success', FAILED: 'error', RUNNING: 'in-progress', QUEUED: 'pending', CANCELLED: 'stopped', INITIALIZED: 'loading' };
        return <StatusIndicator type={statusMap[item.IngestionStatus] || 'info'}>{item.IngestionStatus}</StatusIndicator>;
      },
      width: 150
    },
    {
      id: 'RowsIngested',
      header: 'Rows Ingested',
      cell: item => item.RowInfo?.RowsIngested?.toLocaleString() ?? '—',
      width: 130
    },
    {
      id: 'IngestionTimeInSeconds',
      header: 'Duration (min)',
      cell: item => {
        if (item.IngestionTimeInSeconds == null) return '—';
        const m = Math.floor(item.IngestionTimeInSeconds / 60);
        const s = item.IngestionTimeInSeconds % 60;
        return m > 0 ? `${m}m ${s}s` : `${s}s`;
      },
      width: 130
    },
    {
      id: 'RequestSource',
      header: 'Source',
      cell: item => {
        if (item.RequestSource === 'MANUAL' && item.IngestionId) {
          const parts = item.IngestionId.split('-');
          if (parts[0] === 'CRUZER') return `CRUZER-${parts[1]}`;
          return parts[0];
        }
        if (item.RequestSource === 'SCHEDULED') return 'QS SCHEDULE';
        return item.RequestSource || '—';
      },
      width: 120
    }
  ];

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header={`Refresh History — ${schedule?.datasetUrl || ''}`}
      size="large"
      footer={
        <Box float="right">
          <Button variant="primary" onClick={onDismiss}>Close</Button>
        </Box>
      }
    >
      <SpaceBetween size="xs">
        {error && <Alert type="error">{error}</Alert>}
        <Table
          columnDefinitions={columnDefinitions}
          items={history}
          loading={loading}
          loadingText="Loading history..."
          empty={
            <Box textAlign="center" color="inherit">
              <b>No history found</b>
            </Box>
          }
        />
        {nextToken && (
          <Box textAlign="center" padding={{ top: 's', bottom: 'xs' }}>
            <Button
              onClick={() => fetchIngestions(nextToken, true)}
              loading={loadingMore}
              iconName="angle-down"
              iconAlign="right"
            >
              Load more results
            </Button>
          </Box>
        )}
      </SpaceBetween>
    </Modal>
  );
};

export default RefreshHistoryModal;
